import { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import CustomTabs from 'components/CustomTabs';
import { CUSTOM_TABS_DIRECTION, POP_UP_TYPE, TRANSACTION_STATUS } from 'components/constants';
import DetailsTab from './components/DetailsTab';
import MonitoringCheckTab from './components/MonitoringCheckTab';
import DocumentsTab from './components/DocumentsTab';
import WebhooksTab from './components/WebhooksTab';
import { paymentTransactionDocumentRequestDto } from './components/PaymentTransactionDocumentRequestDto';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { PopConfirmationScheme } from 'components/PopUpScheme/PopConfirmationScheme';
import { PopUpAttachDocumentScheme } from 'components/PopUpScheme/PopUpAttachDocumentScheme';
import { Container } from 'uikit/Container/Container';
import { PopUp } from 'uikit/PopUp/PopUp';
import './TransactionDetails.scss';
import { ROUTE_PATHS } from '../../../../routes/constants';

const popUpInitialData = {
  isShowPopUp: false,
  popUpType: '',
  title: '',
  description: '',
  confirmButtonLabel: '',
  cancelButtonLabel: '',
  onCancel: () => {},
  onConfirm: () => {}
};

const TRANSACTION_TABS = {
  DETAILS: 'details',
  MONITORING_CHECK: 'monitoring-check',
  DOCUMENTS: 'documents',
  WEBHOOKS: 'webhooks'
};

const TransactionDetails = ({ transactionsStore, operatorsStore }) => {
  const i18n = useContext(i18nContext);
  const { id: transactionNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [popUpData, setPopUpData] = useState(popUpInitialData);
  const [isOpenAttachDocumentPopUp, setIsOpenAttachDocumentPopUp] = useState(false);
  const [editedDocument, setEditedDocument] = useState(null);
  const [activeTab, setActiveTab] = useState(TRANSACTION_TABS.DETAILS);

  // Tab configuration with potential for role-based access control
  const tabList = useMemo(
    () => [
      {
        name: TRANSACTION_TABS.DETAILS,
        title: i18n.getMessage('transactionDetails.tab.details'),
        icon: null,
        isDisabled: false // Can be modified based on roles
      },
      {
        name: TRANSACTION_TABS.MONITORING_CHECK,
        title: i18n.getMessage('transactionDetails.tab.monitoringCheck'),
        icon: null,
        isDisabled: false // Can be modified based on roles
      },
      {
        name: TRANSACTION_TABS.DOCUMENTS,
        title: i18n.getMessage('transactionDetails.tab.documents'),
        icon: null,
        isDisabled: false // Can be modified based on roles
      },
      {
        name: TRANSACTION_TABS.WEBHOOKS,
        title: i18n.getMessage('transactionDetails.tab.webhooks'),
        icon: null,
        isDisabled: false
      }
    ],
    [i18n]
  );

  useEffect(() => {
    transactionsStore.resetTransactionStore();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (transactionNumber) {
      transactionsStore.getTransaction(transactionNumber);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionNumber]);

  // Handle tab navigation via query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    if (tabParam && Object.values(TRANSACTION_TABS).includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (!tabParam) {
      // Default to details tab if no tab specified
      setActiveTab(TRANSACTION_TABS.DETAILS);
    }
  }, [location.search]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tabName);
    navigate(`${ROUTE_PATHS.TRANSACTIONS}/${transactionNumber}?${searchParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    if (
      transactionsStore.isTransactionReturned &&
      transactionNumber !== transactionsStore.currentTransactionDetails?.transactionNumber
    ) {
      navigate(`${ROUTE_PATHS.TRANSACTIONS}/${transactionsStore.currentTransactionDetails?.transactionNumber}`);
      transactionsStore.setIsTransactionReturned(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.isTransactionReturned]);

  const resetPopUpData = () => setPopUpData(popUpInitialData);

  const handleAcceptOnHold = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.accept.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.accept.onHold.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.acceptOnHoldTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleAcceptProcessing = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.accept.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.accept.processing.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.acceptProcessingTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleReject = () => {
    const description =
      transactionsStore.currentTransactionDetails.status === TRANSACTION_STATUS.ON_HOLD
        ? i18n.getMessage('transactionDetails.confirmModal.reject.onHold.message')
        : i18n.getMessage('transactionDetails.confirmModal.reject.processing.message');

    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.reject.title'),
      description: description,
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.rejectTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleAcceptDelayedTransaction = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.acceptDelayedTransaction.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.acceptDelayedTransaction.processing.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.acceptDelayedTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleRejectDelayedTransaction = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.rejectDelayedTransaction.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.rejectDelayedTransaction.processing.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.rejectDelayedTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleCreateIncomingExchange = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.createIncomingExchange.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.createIncomingExchange.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.createIncomingExchange(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleBoostCryptoTransaction = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.boost.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.boost.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.boostCryptoTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleUnfreezeCryptoTransaction = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.unfreeze.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.unfreeze.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.unfreezeCryptoTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleReturn = () => {
    const description =
      transactionsStore.currentTransactionDetails.status === TRANSACTION_STATUS.ON_HOLD
        ? i18n.getMessage('transactionDetails.confirmModal.return.onHold.message')
        : i18n.getMessage('transactionDetails.confirmModal.return.paid.message');

    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.return.title'),
      description: description,
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.returnTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleRetry = () => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.CONFIRMATION,
      title: i18n.getMessage('transactionDetails.confirmModal.retry.title'),
      description: i18n.getMessage('transactionDetails.confirmModal.retry.message'),
      confirmButtonLabel: i18n.getMessage('transactionDetails.confirmModal.confirm'),
      cancelButtonLabel: i18n.getMessage('transactionDetails.confirmModal.cancel'),
      onConfirm: () => transactionsStore.retryTransaction(transactionNumber).then(() => resetPopUpData()),
      onCancel: resetPopUpData
    });
  };

  const handleChangeDocumentStatus = async (name, status) => {
    await transactionsStore.changeTransactionDocumentStatus(transactionNumber, status);
  };

  const handleSaveMlroApproval = async (status) => {
    await transactionsStore.updateTransactionMlroApproval(transactionNumber, status);
  };

  const handleLoadTransactionNotes = async () => {
    await transactionsStore.getTransactionNotes(transactionNumber);
  };

  const handleAddTransactionNote = async (transactionNumber, note, assigneeId, documents) => {
    await transactionsStore.addTransactionNote(
      transactionsStore?.currentTransactionDetails?.accountNumber,
      transactionNumber,
      note,
      assigneeId,
      documents
    );
  };

  const handleDeleteTransactionNote = async (noteId) => {
    await transactionsStore.deleteTransactionNote(noteId);
  };

  const handleDownloadDocument = (documentId) => transactionsStore.downloadTransactionNoteDocument(documentId);

  const handleOpenAttachDocumentPopUp = (document = null) => {
    if (document) {
      setEditedDocument(document);
    }
    setIsOpenAttachDocumentPopUp(true);
  };
  const handleCloseAttachDocumentPopUp = () => {
    setIsOpenAttachDocumentPopUp(false);
    setEditedDocument(null);
  };

  const handleAttachPaymentTransactionDocument = async (document, requestDto) => {
    const data = new FormData();
    data.append(
      'dto',
      new Blob(
        [JSON.stringify(paymentTransactionDocumentRequestDto(requestDto.hidden, requestDto.category, requestDto.note))],
        {
          type: 'application/json'
        }
      )
    );
    data.append('file', document);
    await transactionsStore.attachPaymentTransactionDocument(transactionNumber, data);
  };

  useEffect(() => {
    if (transactionsStore.isTransactionDocumentAttachedOrUpdated) {
      handleCloseAttachDocumentPopUp();
      transactionsStore.setIsTransactionDocumentAttachedOrUpdated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsStore.isTransactionDocumentAttachedOrUpdated]);

  if (!transactionsStore.currentTransactionDetails) {
    return <Loader className={'application-loader transaction-details-loader'} />;
  }

  const renderTabContent = () => {
    const commonProps = {
      isLoading: transactionsStore.isLoading,
      transactionNumber: transactionNumber,
      notes: transactionsStore.currentTransactionNotes,
      handleLoadNotes: handleLoadTransactionNotes,
      handleAddNote: handleAddTransactionNote,
      handleDeleteNote: handleDeleteTransactionNote,
      handleDownloadDocument: handleDownloadDocument
    };

    switch (activeTab) {
      case TRANSACTION_TABS.DETAILS:
        return (
          <DetailsTab
            {...commonProps}
            transactionData={transactionsStore.currentTransactionDetails}
            handleAcceptOnHold={handleAcceptOnHold}
            handleAcceptProcessing={handleAcceptProcessing}
            handleReject={handleReject}
            handleCreateIncomingExchange={handleCreateIncomingExchange}
            handleReturn={handleReturn}
            handleRetry={handleRetry}
            handleChangeDocumentStatus={handleChangeDocumentStatus}
            handleSaveMlroApproval={handleSaveMlroApproval}
            handleOpenAttachDocumentPopUp={handleOpenAttachDocumentPopUp}
            downloadTransactionDocument={transactionsStore.downloadPaymentTransactionDocument}
            deleteTransactionDocument={transactionsStore.deletePaymentTransactionDocument}
            handleBoostCryptoTransaction={handleBoostCryptoTransaction}
            handleUnfreezeCryptoTransaction={handleUnfreezeCryptoTransaction}
            handleAcceptDelayedTransaction={handleAcceptDelayedTransaction}
            handleRejectDelayedTransaction={handleRejectDelayedTransaction}
          />
        );
      case TRANSACTION_TABS.MONITORING_CHECK:
        return (
          <MonitoringCheckTab
            {...commonProps}
            transactionData={transactionsStore.currentTransactionDetails}
            counterpartyVerification={
              transactionsStore.currentTransactionDetails?.transactionMonitoringDetails?.counterpartyVerification
            }
            paymentLimits={transactionsStore.currentTransactionDetails?.transactionMonitoringDetails?.paymentLimits}
            sanctions={transactionsStore.currentTransactionDetails?.transactionMonitoringDetails?.sanctionDetails}
            blacklistDetails={
              transactionsStore.currentTransactionDetails?.transactionMonitoringDetails?.blacklistDetails
            }
            ruleEngineScoring={
              transactionsStore.currentTransactionDetails?.transactionMonitoringDetails?.ruleEngineScoring
            }
          />
        );
      case TRANSACTION_TABS.DOCUMENTS:
        return (
          <DocumentsTab
            {...commonProps}
            transactionData={transactionsStore.currentTransactionDetails}
            downloadTransactionDocument={transactionsStore.downloadPaymentTransactionDocument}
            deleteTransactionDocument={transactionsStore.deletePaymentTransactionDocument}
            updateTransactionDocument={transactionsStore.updatePaymentDocumentDetails}
            operatorsStore={operatorsStore}
          />
        );
      case TRANSACTION_TABS.WEBHOOKS:
        return <WebhooksTab transactionNumber={transactionNumber} />;
      default:
        return null;
    }
  };

  return (
    <Container className={'transaction-details-container'} header={i18n.getMessage('container.transactionDetails')}>
      <CustomTabs
        className={'transaction-details-tabs'}
        tabList={tabList}
        direction={CUSTOM_TABS_DIRECTION.HORIZONTAL}
        activeTab={activeTab}
        setActiveTab={handleTabChange}
      />

      <div className={'transaction-details-tab-content'}>{renderTabContent()}</div>

      <PopUp alignOnCenter show={popUpData.isShowPopUp || isOpenAttachDocumentPopUp}>
        {popUpData.isShowPopUp && (
          <PopConfirmationScheme
            title={popUpData.title}
            description={popUpData.description}
            onConfirmButtonTitle={popUpData.confirmButtonLabel}
            onCloseButtonTitle={popUpData.cancelButtonLabel}
            onConfirm={popUpData.onConfirm}
            onClose={popUpData.onCancel}
          />
        )}
        {isOpenAttachDocumentPopUp && (
          <PopUpAttachDocumentScheme
            isLoading={transactionsStore.isLoading}
            handleClose={handleCloseAttachDocumentPopUp}
            editedDocument={editedDocument}
            handleAttachTransactionDocument={handleAttachPaymentTransactionDocument}
            handleUpdateTransactionDocument={transactionsStore.updatePaymentDocumentDetails}
          />
        )}
      </PopUp>
    </Container>
  );
};

TransactionDetails.propTypes = {
  customerNumber: PropTypes.string,
  userInfo: PropTypes.object,
  isInitialized: PropTypes.bool,
  isUserDeletionAvailable: PropTypes.bool,
  usersStore: MobXPropTypes.observableObject,
  transactionsStore: MobXPropTypes.observableObject,
  operatorsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  usersStore: stores.usersStore,
  transactionsStore: stores.transactionsStore,
  operatorsStore: stores.operatorsStore
}))(observer(TransactionDetails));
